import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPencil } from '@fortawesome/pro-light-svg-icons'

import {
  Box,
  Button,
  DashboardModule,
  FileUploadZone,
  Form,
  Grid,
  SidebarNoticeBox,
  StatusBadge,
} from '@domain-group/suit-theme'

import OrderItemHeader from '@components/OrderItemHeader'

import { statusColors } from '@functions/status'

import type { OrderItemModel } from '@models/types'

import AttachmentPreview from './components/AttachmentPreview'
import AttachmentTile from './components/AttachmentTile'

import useProviderCampaignMedia from './hooks/useProviderCampaignMedia'

type OrderItemProps = {
  campaignTitle: string,
  orderItem: OrderItemModel,
}

const OrderItem = (params: OrderItemProps) => {
  const { campaignTitle, orderItem } = params

  const providerCampaignMedia = useProviderCampaignMedia({ orderItem })
  const {
    callbacks: {
      manageProviderOrderItemStatusModal,
    },
    groupedAttachments,
    hasAdditionalInstructions,
    hasDeliveredAttachments,
    hasRevisedAttachments,
    selectedAttachmentId,
    service,
    status,
    uppy,
  } = providerCampaignMedia

  return (
    <DashboardModule
      contentBoxProps={{ flexDirection: 'column' }}
      customHeader={(
        <OrderItemHeader
          boxProps={{
            borderBottom: hasAdditionalInstructions ? 'none' : '1px solid',
          }}
          orderItem={orderItem}
          // @ts-expect-error TS2739
          service={service}
        >
          <StatusBadge
            boxProps={{ margin: 'auto', marginRight: 'large' }}
            ghost
            color={statusColors[status?.key as keyof typeof statusColors] ?? 'pending'}
            text={status?.title}
          />
        </OrderItemHeader>
      )}
    >
      {hasAdditionalInstructions && (
        <Box borderBottom="1px solid" borderColor="lineColor" paddingBottom="large" paddingX="large">
          <SidebarNoticeBox boxProps={{ marginBottom: 'none' }}>
            <SidebarNoticeBox.Title>
              Additional Instructions
            </SidebarNoticeBox.Title>

            <SidebarNoticeBox.Paragraph>
              {orderItem.additional_instructions}
            </SidebarNoticeBox.Paragraph>
          </SidebarNoticeBox>
        </Box>
      )}

      {!!selectedAttachmentId && (
        <AttachmentPreview
          orderItemPayload={{ ...providerCampaignMedia, campaignTitle }}
        />
      )}

      {hasDeliveredAttachments && (
        <Form.Field boxProps={{ paddingX: 'large' }} label="Latest Version" marginTop="large">
          <Grid
            // @ts-expect-error TS2322
            gridGap="medium"
            gridAutoRows="1fr"
            gridTemplateColumns="repeat(auto-fill, minmax(180px, 1fr))"
            gridTemplateRows="repeat(auto-fill, minmax(140px, 1fr))"
          >
            {groupedAttachments.delivered.map((attachment, index) => (
              <AttachmentTile
                attachment={attachment}
                index={index}
                isSelected={attachment.id === selectedAttachmentId}
                key={attachment.id}
                orderItemPayload={providerCampaignMedia}
              />
            ))}
          </Grid>
        </Form.Field>
      )}

      {hasRevisedAttachments && (
        <Form.Field boxProps={{ marginX: 'large' }} label="Old Version" marginTop="large">
          <Grid
            // @ts-expect-error TS2322
            gridGap="medium"
            gridAutoRows="1fr"
            gridTemplateColumns="repeat(auto-fill, minmax(180px, 1fr))"
            gridTemplateRows="repeat(auto-fill, minmax(140px, 1fr))"
          >
            {groupedAttachments.revised.map((attachment, index) => (
              <AttachmentTile
                attachment={attachment}
                index={index}
                isSelected={attachment.id === selectedAttachmentId}
                key={attachment.id}
                orderItemPayload={providerCampaignMedia}
              />
            ))}
          </Grid>
        </Form.Field>
      )}

      <Box padding="large">
        <FileUploadZone uppy={uppy} />
      </Box>

      <DashboardModule.Footer>
        <DashboardModule.FooterRight>
          <Button
            buttonStyle="secondaryEdit"
            icon={<FontAwesomeIcon icon={faPencil} />}
            onClick={() => manageProviderOrderItemStatusModal()}
            // @ts-expect-error TS2322
            size={['medium', 'small']}
            width={['100%', 'fit-content']}
          >
            Manage Order Item
          </Button>
        </DashboardModule.FooterRight>
      </DashboardModule.Footer>
    </DashboardModule>
  )
}

export default OrderItem
